/**
 * Persisted player state and its schema migrations. The server is the source of truth
 * (ADR 0002): this is the last server snapshot, the local prediction on top of it, and the
 * outbox of ops still to sync through POST /v1/sync. There is no client-side ledger: when
 * the outbox is empty the next snapshot simply replaces every server-owned field.
 */
import type { DailyGiftState, StreakState } from '@mp/core';
import type { InterstitialState } from '@mp/adapters/ads';
import type { RoundSubmissionDto } from '@mp/schemas';
import { BOOSTERS, economy, type BoosterKey } from '../config/content.js';
import type { LinkResultDto, RoundBody, SyncOp } from './dto.js';
import { uuid } from './ids.js';
import { defaultReviewState, type ReviewState } from './review.js';

export const STATE_VERSION = 4;

export type Style = 'a' | 'b' | 'c';

export interface LevelBest {
  bestStars: number;
  bestTaps: number;
  plays: number;
}

export interface Settings {
  sound: boolean;
  music: boolean;
  vibration: boolean;
  colorBlind: boolean;
  /**
   * Clearer items (accessibility, roadmap-3k §11): clutter items picked for looking like the order's
   * are drawn with their level's substitute art (views/looks.ts). Absent until the player taps the
   * switch, and then it follows `colorBlind` (clearerItemsOn); only their own tap ever writes it, on this
   * device or another (it follows the account: services/sync.ts reconcile restores the server's copy).
   */
  clearerItems?: boolean;
  reducedMotion: boolean;
  locale: string;
  /**
   * Settings › City outfits (launch plan Phase 1 step 6): Pip dresses in each city's own outfit
   * while he wears no wardrobe outfit (services/cityOutfit.ts). On by default; it follows the
   * account like Clearer items (services/ops.ts settingsBody sends it, services/sync.ts reconcile restores it).
   */
  cityOutfits: boolean;
}

/**
 * Whether lookalike clutter is drawn with its substitute art: the player's own choice, and until
 * they make one the colour-blind setting's. Roadmap-3k §11 names colour-blind players among those
 * lookalike clutter hurts (it is picked by silhouette, which the patterned art does not change),
 * so they get the clearer art without having to find a second switch. It is derived, not copied
 * when the patterns go on: turning them off again leaves nothing behind, and a choice the player
 * made, either way, is never overwritten. A state saved before the setting existed has made none.
 */
export function clearerItemsOn(s: Partial<Pick<Settings, 'clearerItems' | 'colorBlind'>>): boolean {
  return s.clearerItems ?? s.colorBlind ?? false;
}

export interface Auth {
  playerId: string;
  accessToken: string;
  refreshToken: string;
  /** epoch ms */
  expiresAt: number;
  /** when this session (refresh family) began: guest start, sign-in, transfer; refreshes keep it */
  sessionAt?: number;
}

/** One queued change, in the exact shape POST /v1/sync takes (plus local bookkeeping). */
export type OutboxOp = SyncOp & {
  /** a round waiting on its win screen (double-tips nonce); sync stops before held ops */
  held?: boolean;
  /** a round sent again once after its level was refreshed (level_outdated); never retried twice */
  resent?: boolean;
};

export interface Flags {
  freeRescueUsed: boolean;
  /** first-session steps done (Flow A); also analytics tutorial_step names */
  tutorial: string[];
  seenMechanics: string[];
  seenUnlocks: string[];
  grandOpenings: string[];
  savePrompts: number;
  /** roadmap-3k city moments already shown (services/cityRoles.ts): `open:<city>`, `finale:<shop>`, `remind:<city>:<mechanic>`, `end` */
  cityCards: string[];
}

/** One-shot and rate-limited prompts. */
export interface Prompts {
  /** Flow G web-to-app (OVL-05): times shown and when */
  webToApp: { count: number; lastAt: number | null };
  /** the Google Play banner on Android web (features/webToApp): times shown and when, counted apart from the modal's */
  webToAppBanner: { count: number; lastAt: number | null };
  /** the store rating sheet's pacing (services/review.ts); local, like every prompt */
  review: ReviewState;
  /** MO-03.2 Starter Pack offer (OVL-06) shown once */
  starterShownAt: number | null;
  /** streak milestones already celebrated */
  streakSeen: number[];
  /** CP-01: last league week whose results card was shown */
  leagueSeenWeek: number | null;
  /** consent (OVL-08) answered for this version */
  consentAsked: number | null;
  /** birth year delivered to POST /v1/age */
  ageSent: boolean;
  /** inbox items already celebrated with OVL-09 */
  inboxSeen: string[];
  /** unclaimed inbox items at the last fetch (map badge) */
  inboxUnread: number;
}

/** SO-02: today's daily puzzle as far as this device knows. */
export interface DailyPuzzleState {
  date: string;
  roundId: string;
  result: LinkResultDto;
  /** submission not yet accepted by the server (offline); `linkId` when played from a friend's daily link */
  pending: { round: RoundBody; localDate: string; linkId?: string } | null;
  linkUrl: string | null;
}

export interface PlayerState {
  v: number;
  deviceId: string;
  createdAt: number;
  auth: Auth | null;
  city: string;
  /**
   * MT-01 journey: the city the street map was last on, where the next start opens it (while the
   * player can still be there). Device-local: the server keeps no such field, and a new device
   * starts at the furthest open city with levels to beat.
   */
  lastCity: string | null;
  coins: number;
  gems: number;
  /** lifetime sum of best stars per level; gates upgrades, never spent */
  stars: number;
  xp: number;
  level: number;
  inventory: Record<BoosterKey, number>;
  /**
   * Boosters used in the round being played. The server debits them only when the round is
   * submitted, so a snapshot adopted mid-round (a rescue or booster ad) still counts them:
   * reconcile subtracts these until the round ends.
   */
  roundBoosters?: Partial<Record<BoosterKey, number>>;
  /** SO-01 rare items owned (upg04/upg08 need one) */
  rareItems: Record<string, number>;
  stickers: Record<string, number>;
  levels: Record<string, LevelBest>;
  /** upgrades per shop slot, and decor placed per decor spot (services/decor.ts) */
  shops: Record<string, { upgrades: Record<string, Style>; decor?: Record<string, string> }>;
  /** owned decor (server meta.decor): album and Rush prizes, store pieces */
  decor: Record<string, number>;
  shopName: string | null;
  flags: Flags;
  settings: Settings;
  outbox: OutboxOp[];
  daily: DailyGiftState;
  streak: StreakState;
  /** MT-03 offline sales accrue from here (server meta.offline.lastCollectAt) */
  lastCollectAt: number;
  /** epoch ms of the last time the app was in the foreground */
  lastSeenAt: number;
  /** GP-09 consecutive fails per level */
  failStreak: Record<string, number>;
  /** MO-02 interstitial pacing (adapters InterstitialScheduler state) */
  interstitial: InterstitialState;
  /** any purchase ever (server meta.payer): no interstitials */
  payer: boolean;
  /** SY-02.1 COPPA mode (server meta.coppa or the local age gate) */
  coppa: boolean;
  parentVerified: boolean;
  /** SY-02.1 age gate answer (never sent before it is asked) */
  birthYear: number | null;
  consentVersion: number | null;
  /** SY-01 linked identities (server meta.linked) */
  linked: string[];
  costume: string;
  /** the look Pip wears (economy.skins; server meta.skin), kept offline like the costume */
  skin: string;
  /** SO-05 attribution of the link that installed the game, for guest auth */
  attribution: { ref?: string; campaign?: string } | null;
  /**
   * Where the install came from, in Play install-referrer form (`utm_source=…&gclid=…`): the Play referrer on
   * Android, the first visit's utm_* on the web. Kept until guest creation succeeds (a failed first auth must
   * not lose it), then cleared: it is sent once (services/installReferrer.ts).
   */
  installReferrer: string | null;
  prompts: Prompts;
  dailyPuzzle: DailyPuzzleState | null;
  /** last server snapshot time (ms) */
  syncedAt: number | null;
}

export function defaultState(deviceId: string, now: number): PlayerState {
  const inv = {} as Record<BoosterKey, number>;
  for (const b of BOOSTERS) inv[b] = economy.start.boosters[b] ?? 0;
  return {
    v: STATE_VERSION,
    deviceId,
    createdAt: now,
    auth: null,
    city: 'ny',
    lastCity: null,
    coins: economy.start.coins,
    gems: economy.start.gems,
    stars: 0,
    xp: 0,
    level: 1,
    inventory: inv,
    rareItems: {},
    stickers: {},
    levels: {},
    shops: {},
    decor: {},
    shopName: null,
    flags: { freeRescueUsed: false, tutorial: [], seenMechanics: [], seenUnlocks: [], grandOpenings: [], savePrompts: 0, cityCards: [] },
    settings: { sound: true, music: true, vibration: true, colorBlind: false, reducedMotion: false, locale: 'en', cityOutfits: true },
    outbox: [],
    daily: { lastClaimDay: null, cycleDay: 0 },
    streak: { count: 0, lastDay: null, restWeek: 0, restUsed: 0 },
    lastCollectAt: now,
    lastSeenAt: now,
    failStreak: {},
    interstitial: { roundsSinceLast: 0, lastShownAt: null, lastRewardedAt: null },
    payer: false,
    coppa: false,
    parentVerified: false,
    birthYear: null,
    consentVersion: null,
    linked: [],
    costume: 'classic',
    skin: 'classic',
    attribution: null,
    installReferrer: null,
    prompts: { webToApp: { count: 0, lastAt: null }, webToAppBanner: { count: 0, lastAt: null }, review: defaultReviewState(), starterShownAt: null, streakSeen: [], leagueSeenWeek: null, consentAsked: null, ageSent: false, inboxSeen: [], inboxUnread: 0 },
    dailyPuzzle: null,
    syncedAt: null,
  };
}

type Raw = Record<string, unknown>;

/**
 * Migrations from version n to n+1. Every persisted shape that ever shipped stays migratable.
 *  1 → 2: the pending-rounds queue became a general outbox (rounds, upgrades, shop name).
 *  2 → 3: client-only grants moved to an explicit ledger; `tutorialStep` number became a step list.
 *  3 → 4: R1 server-side economy. The ledger is dropped (the server snapshot wins) and the
 *         outbox takes the /v1/sync SyncOp shape with uuid op ids; offline sales accrue from
 *         `lastCollectAt`.
 */
export const MIGRATIONS: Record<number, (s: Raw) => Raw> = {
  1: (s) => {
    const pending = (s.pendingRounds as RoundSubmissionDto[] | undefined) ?? [];
    const { pendingRounds: _drop, ...rest } = s;
    return { ...rest, v: 2, outbox: pending.map((body) => ({ kind: 'round', id: body.roundId, body })) };
  },
  2: (s) => {
    const flags = (s.flags as Raw | undefined) ?? {};
    const step = typeof flags.tutorialStep === 'number' ? flags.tutorialStep : 0;
    const { tutorialStep: _drop, ...restFlags } = flags;
    const legacySteps = ['l1_win', 'l2_win', 'l3_win', 'map_reveal'].slice(0, step);
    return { ...s, v: 3, ledger: [], flags: { ...restFlags, tutorial: legacySteps } };
  },
  3: (s) => {
    const { ledger: _drop, adViews: _ads, ...rest } = s;
    const old = (s.outbox as Raw[] | undefined) ?? [];
    const outbox: Raw[] = [];
    for (const o of old) {
      const body = (o.body ?? {}) as Raw;
      if (o.kind === 'round') outbox.push({ opId: typeof body.roundId === 'string' ? body.roundId : uuid(), kind: 'round', body });
      else if (o.kind === 'upgrade') outbox.push({ opId: uuid(), kind: 'upgrade', body: { shopId: o.shop, slot: body.slot, style: body.style } });
      else if (o.kind === 'shopName') outbox.push({ opId: uuid(), kind: 'shopName', body: { name: body.name } });
    }
    const lastSeenAt = typeof s.lastSeenAt === 'number' ? s.lastSeenAt : Date.now();
    return { ...rest, v: 4, outbox, lastCollectAt: lastSeenAt };
  },
};

/** Bring any persisted state up to STATE_VERSION, filling fields added since with defaults. */
export function migrate(raw: Raw, deviceId: string, now: number): PlayerState {
  let s: Raw = { ...raw };
  let v = typeof s.v === 'number' ? s.v : 1;
  while (v < STATE_VERSION) {
    const step = MIGRATIONS[v];
    if (!step) throw new Error(`no migration from state v${v}`);
    s = step(s);
    v = s.v as number;
  }
  const base = defaultState((s.deviceId as string) ?? deviceId, now);
  const merged = { ...base, ...s } as PlayerState;
  merged.flags = { ...base.flags, ...(s.flags as Partial<Flags>) };
  merged.settings = { ...base.settings, ...(s.settings as Partial<Settings>) };
  merged.inventory = { ...base.inventory, ...(s.inventory as Partial<Record<BoosterKey, number>>) };
  merged.prompts = { ...base.prompts, ...(s.prompts as Partial<Prompts>) };
  merged.interstitial = { ...base.interstitial, ...(s.interstitial as Partial<InterstitialState>) };
  merged.v = STATE_VERSION;
  return merged;
}
